import React from 'react';
import type {MessageDescriptor} from 'react-intl';
import {defineMessages} from 'react-intl';
import {
    type FilterGroupNewFilter,
    type FilterProps,
    type FiltersProps,
    type MultiKeyFilterTextValue,
    type QueryFilterAdapterModel,
    type QueryFilterAdapterProps,
} from '@igp/shared/components/filter';
import {FilterGroupNew, TextInputWithDropdownFilter, withQueryFilter} from '@igp/shared/components/filter';
import {AalFilter} from '@igp/shared/features/bo-user-group';

import type {BoUserFilterModel, BoUserFilterName, BoUserServerFilterKeys, BoUserTextFilterKeys} from '../types';

import {RolesFilter} from './RolesFilter';

const localized = defineMessages({
    all: {
        id: 'BoUserFilters_all',
        defaultMessage: 'All',
    },
    username: {
        id: 'BoUserFilters_username',
        defaultMessage: 'Username',
    },
    usernamePlaceholder: {
        id: 'BoUserFilters_usernamePlaceholder',
        defaultMessage: 'Search by username',
    },
    firstName: {
        id: 'BoUserFilters_firstName',
        defaultMessage: 'First Name',
    },
    firstNamePlaceholder: {
        id: 'BoUserFilters_firstNamePlaceholder',
        defaultMessage: 'Search by first name',
    },
    lastName: {
        id: 'BoUserFilters_lastName',
        defaultMessage: 'Last Name',
    },
    lastNamePlaceholder: {
        id: 'BoUserFilters_lastNamePlaceholder',
        defaultMessage: 'Search by last name',
    },
    email: {
        id: 'BoUserFilters_email',
        defaultMessage: 'Email',
    },
    emailPlaceholder: {
        id: 'BoUserFilters_emailPlaceholder',
        defaultMessage: 'Search by email',
    },
    playerId: {
        id: 'BoUserFilters_playerId',
        defaultMessage: 'Agent Player ID',
    },
    playerIdPlaceholder: {
        id: 'BoUserFilters_playerIdPlaceholder',
        defaultMessage: 'Search by agent player Id',
    },
    defaultPlaceholder: {
        id: 'BoUserFilters_defaultPlaceholder',
        defaultMessage: 'Search in User Management',
    },
    uuidPlaceholder: {
        id: 'BoUserFilters_uuidPlaceholder',
        defaultMessage: 'Search by exact user id',
    },
    uuid: {
        id: 'BoUserFilters_uuid',
        defaultMessage: 'UUID',
    },
});

function BoUserFilters(props: Omit<FiltersProps<BoUserFilterModel, BoUserFilterName>, 'submitButton'>) {
    function getInputPlaceholder(option: string) {
        const placeholderConfig: Record<BoUserTextFilterKeys | 'uuid', MessageDescriptor> = {
            all: localized.defaultPlaceholder,
            username: localized.usernamePlaceholder,
            first_name: localized.firstNamePlaceholder,
            last_name: localized.lastNamePlaceholder,
            email: localized.emailPlaceholder,
            player_id: localized.playerIdPlaceholder,
            uuid: localized.uuidPlaceholder,
        };
        return placeholderConfig[option as BoUserTextFilterKeys | 'uuid'] ?? localized.defaultPlaceholder;
    }

    const filters: FilterGroupNewFilter<BoUserFilterModel, BoUserFilterName>[] = [
        {
            filterName: 'all-text-filters',
            modelField: 'text',
            component: (props: FilterProps<MultiKeyFilterTextValue<BoUserTextFilterKeys | 'uuid'>>) => (
                <TextInputWithDropdownFilter
                    options={[
                        {label: localized.all, value: 'all'},
                        {label: localized.username, value: 'username'},
                        {label: localized.firstName, value: 'first_name'},
                        {label: localized.lastName, value: 'last_name'},
                        {label: localized.email, value: 'email'},
                        {label: localized.playerId, value: 'player_id'},
                        {label: localized.uuid, value: 'uuid'},
                    ]}
                    getInputPlaceholder={getInputPlaceholder}
                    width="full"
                    {...props}
                />
            ),
            collapseOnMobile: false,
        },
        {
            filterName: 'role',
            modelField: 'role',
            component: RolesFilter,
            collapseOnMobile: false,
        },
        {
            filterName: 'aal',
            modelField: 'aal',
            component: (props: FilterProps<string[]>) => <AalFilter {...props} filterBy="group_id" />,
            collapseOnMobile: false,
        },
    ];
    return <FilterGroupNew mode="update-on-change" allFilters={filters} {...props} />;
}

function BoUserQueryFiltersAdapter({
    model,
    onChange,
    ...props
}: Omit<QueryFilterAdapterProps<BoUserServerFilterKeys, BoUserFilterName, BoUserTextFilterKeys>, 'submitButton'>) {
    const filterViewModel: BoUserFilterModel = {
        text: model?.text,
        role: model?.role_group_ids as string[],
        aal: model?.aal_group_ids as string[],
    };

    function handleChange(model: BoUserFilterModel) {
        const result: QueryFilterAdapterModel<BoUserServerFilterKeys, BoUserTextFilterKeys> = {
            text: model?.text,
            aal_group_ids: model?.aal,
            role_group_ids: model?.role,
        };
        onChange(result);
    }
    return <BoUserFilters model={filterViewModel} onChange={handleChange} {...props} />;
}

export const BoUserQueryFilters = withQueryFilter<BoUserServerFilterKeys, BoUserFilterName, BoUserTextFilterKeys | 'uuid'>(
    BoUserQueryFiltersAdapter,
    {
        'all-text-filters': ['all', 'username', 'first_name', 'last_name', 'email', 'player_id', 'uuid'],
    }
);
